import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import { deepClone } from '@src/utility/deepClone';
import isEqual from 'lodash/isEqual';

import type { KaraokeConfig } from '../karaoke/KaraokeConfig';
import type {
  CropTransformKeyframe,
  ILayoutSegment,
  LayoutTemplate,
  TextPosition,
  TrackTransforms,
} from './types';

const KARAOKE_SLOT_NAME = 'karaoke';

/**
 * Finds a suitable fallback binding when switching templates.
 * Prefers non-karaoke bindings to avoid leaking transcript track IDs into video slots.
 */
export function findFallbackBinding(
  template: Pick<LayoutTemplate, 'slots'>,
  existingBindings: Record<string, TrackTransforms>,
): TrackTransforms | null {
  const existingEntries = Object.entries(existingBindings);

  if (existingEntries.length === 0) {
    return null;
  }

  const fallbackBinding =
    template.slots
      .filter(slot => slot !== KARAOKE_SLOT_NAME)
      .map(slot => existingBindings[slot])
      .find(Boolean) ??
    existingEntries.find(([slot]) => slot !== KARAOKE_SLOT_NAME)?.[1];

  return fallbackBinding ?? null;
}

/**
 * Builds bindings for a new template by cloning from existing bindings.
 * Preserves element IDs from existing bindings, mapping them to the new template's slots.
 * Falls back to the first available binding if a slot doesn't have a matching binding.
 *
 * Automatically initializes textPosition for karaoke slot:
 * - If template specifies textPosition (split layouts) → use that
 * - If template has no textPosition (basic layouts) → delete override so scene inherits global KaraokeConfig
 *
 * @param template - The layout template to build bindings for
 * @param existingBindings - Current bindings keyed by slot name
 * @param defaultKaraokeConfig - Default karaoke configuration for base positioning values
 * @returns New bindings for all template slots, or null if no existing bindings available
 */
export function buildBindingsForTemplate(
  template: LayoutTemplate,
  existingBindings: ILayoutSegment['bindings'],
  defaultKaraokeConfig: KaraokeConfig | null,
  keyframesCreator?: () => CropTransformKeyframe[],
): ILayoutSegment['bindings'] | null {
  const fallbackBinding = findFallbackBinding(template, existingBindings);

  if (!fallbackBinding) {
    return null;
  }

  const nextBindings: ILayoutSegment['bindings'] = {};

  template.slots.forEach(slot => {
    const sourceBinding = existingBindings[slot] ?? fallbackBinding;
    const clonedBinding = deepClone(sourceBinding);

    // Ensure karaoke slot always targets the dedicated karaoke track even when cloned from a non-karaoke binding
    if (slot === KARAOKE_SLOT_NAME) {
      clonedBinding.trackId = String(KARAOKE_TRACK_NUMBER);
    }

    if (typeof keyframesCreator === 'function') {
      clonedBinding.transformKeyframes = keyframesCreator();
    }

    nextBindings[slot] = clonedBinding;
  });

  // Handle karaoke textPosition based on template
  if (nextBindings.karaoke) {
    const templateTextPosition =
      extractNonDefaultTextPositionFromTemplate(template);

    if (
      nextBindings.karaoke.transformKeyframes &&
      nextBindings.karaoke.transformKeyframes.length > 0
    ) {
      if (templateTextPosition) {
        // Template has textPosition - apply it (split/stacked templates)
        nextBindings.karaoke.transformKeyframes[0].textPosition =
          templateTextPosition;
      } else {
        // Basic templates: no textPosition override — inherit from global KaraokeConfig
        delete nextBindings.karaoke.transformKeyframes[0].textPosition;
      }
    }
  }

  return nextBindings;
}

/**
 * Extracts textPosition from template's karaoke slot if it differs from default.
 * Used when switching templates to initialize textPosition for stacked layouts
 * (e.g., stacked-1-1 has y: 50%, stacked-1-3 has y: 33.333%)
 *
 * @param template - The layout template to extract textPosition from
 * @returns TextPosition if any field is present, null otherwise
 */
export function extractNonDefaultTextPositionFromTemplate(
  template: LayoutTemplate,
): TextPosition | null {
  const karaokeElement = template.layoutElements.find(
    el => el.name === 'karaoke',
  );

  if (!karaokeElement || !karaokeElement.displayElement) {
    return null;
  }

  const displayEl = karaokeElement.displayElement;
  const x = displayEl.x;
  const y = displayEl.y;
  const x_alignment = displayEl.x_alignment;

  // If no positioning fields are specified, don't extract anything
  if (x == null && y == null && x_alignment == null) {
    return null;
  }

  // Return only the fields that are present
  return {
    ...(x != null && { x }),
    ...(y != null && { y }),
    ...(x_alignment != null && { x_alignment }),
  };
}

export function areLayoutsEqual(
  layoutA: ILayoutSegment,
  layoutB: ILayoutSegment,
): boolean {
  if (layoutA.layoutTemplateId !== layoutB.layoutTemplateId) {
    return false;
  }

  const bindingsA = layoutA.bindings;
  const bindingsB = layoutB.bindings;

  const slotsA = Object.keys(bindingsA);
  const slotsB = Object.keys(bindingsB);

  if (slotsA.length !== slotsB.length) {
    return false;
  }

  for (const slot of slotsA) {
    if (!(slot in bindingsB)) {
      return false;
    }

    const bindingA = bindingsA[slot];
    const bindingB = bindingsB[slot];

    if (bindingA.trackId !== bindingB.trackId) {
      return false;
    }

    // Deep compare transformKeyframes
    if (!isEqual(bindingA.transformKeyframes, bindingB.transformKeyframes)) {
      return false;
    }
  }

  return true;
}
